import EnvelopeIcon from '@assets/svg/EnvelopeIcon';
import RenderIf from '@components/common/render-if';
import Form from '@components/dataEntry/form';
import FormInput from '@components/dataEntry/form-input';
import Button from '@components/general/button';
import { EyeIcon, EyeOffIcon, LockClosedIcon } from '@heroicons/react/outline';
import { toastNotification } from '@hooks/toast-notification';
import { useMutationSideEffects } from '@hooks/use-mutation-side-effect';
import useRequest from '@services/api';
import { queryString } from '@services/api/utils/query-string';
import SimpleLayout from 'layout/SimpleLayout';

import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import * as yup from 'yup';

const validationSchema = yup.object({
  email: yup.string().required('Please enter an email.'),
  password: yup.string().required('Please enter a password.')
});

const SignIn = () => {
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const router = useRouter();
  const { data } = useRequest.user.get({ retry: false });
  const [errorsServerValidation, setErrorsServerValidation] = useState({});
  const { mutateAsync: login } = useRequest.user.login();

  const defaultValues = {
    email: '',
    password: ''
  };

  // actions
  const { handleSubmit, formId, loading } = useMutationSideEffects({
    setErrorsServerValidation,
    showToastMessage: false,
    onSubmit: async (inputs) => {
      try {
        const res = await login({ ...inputs });
        toastNotification({ description: 'Signed in successfully!', type: 'success' });
        localStorage.setItem('token', res.headers.authorization.split(' ')[1]);
        await router.push(`/dashboard${queryString(router.query)}`);
      } catch (err) {
        const errors = err.response.data.errors;
        setErrorsServerValidation(errors);
        toastNotification({
          type: 'error',
          description: 'Invalid email or password.'
        });
      }
    }
  });

  useEffect(() => {
    if (data && data.user) router.push('/dashboard');
  }, [data]);

  const EmailLeftIcon = () => (
    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
      <EnvelopeIcon className="text-primary-grey-300 w-4 h-4" />
    </div>
  );

  const PasswordLeftIcon = () => (
    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
      <LockClosedIcon className="text-primary-grey-300 w-4 h-4" />
    </div>
  );

  const PasswordRightIcon = ({ show, setShow }) => (
    <>
      <RenderIf isTrue={!show}>
        <div
          className="absolute inset-y-0 right-0 flex items-center pr-3"
          onClick={() => setShow(true)}
        >
          <EyeOffIcon className="text-primary-grey-300 w-4 h-4 cursor-pointer" />
        </div>
      </RenderIf>
      <RenderIf isTrue={show}>
        <div
          className="absolute inset-y-0 right-0 flex items-center pr-3"
          onClick={() => setShow(false)}
        >
          <EyeIcon className="text-primary-grey-300 w-4 h-4 cursor-pointer" />
        </div>
      </RenderIf>
    </>
  );

  const PasswordIconProps = {
    LeftIcon: () => <PasswordLeftIcon />,
    RightIcon: () => (
      <PasswordRightIcon show={showConfirmPassword} setShow={setShowConfirmPassword} />
    )
  };

  const EmailIconProps = {
    LeftIcon: () => <EmailLeftIcon />
  };

  return (
    <div className="flex justify-center items-center w-full h-[calc(100vh-4.25rem)]">
      <div className="flex flex-col justify-center">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <h3 className="text-[40px] font-extrabold text-primary-navy-500">Welcome Back!</h3>
        </div>
        <p className="text-base text-primary-grey-500">
          Please enter your details to sign into your account
        </p>
        <div className="mt-4 sm:mx-auto sm:w-full sm:max-w-md">
          <div>
            <Form
              defaultValues={defaultValues}
              errorsServerValidation={errorsServerValidation}
              validationSchema={validationSchema}
              onEnterSubmit
              onSubmit={handleSubmit}
              formId={formId}
            >
              <>
                <FormInput
                  label="Email"
                  name="email"
                  type="email"
                  inputClass="mt-2 pl-10"
                  placeholder="Enter your email"
                  IconProps={EmailIconProps}
                />
                <FormInput
                  label="Password"
                  name="password"
                  containerClass="mt-5"
                  inputClass="mt-2 px-10"
                  placeholder="Enter your password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  IconProps={PasswordIconProps}
                />
                <div className="mt-2 flex items-center w-full">
                  <div className="flex items-center w-1/2"></div>
                  <div className="text-sm  w-1/2 text-right">
                    <Link href="/forgot-password">
                      <a className="font-medium text-primary-grey-500 focus:outline-none focus:underline transition ease-in-out duration-150">
                        Forgot password?
                      </a>
                    </Link>
                  </div>
                </div>
                <div className="mt-2">
                  <span className="block w-full rounded-md shadow-sm">
                    <Button
                      type="submit"
                      disabled={loading}
                      variant="primary"
                      loading={loading}
                      className="w-full justify-center h-12"
                      size="md"
                    >
                      Login
                    </Button>
                  </span>
                </div>
              </>
            </Form>

            <div className="mt-4">
              <div className="relative">
                <div className="relative flex justify-center text-xs ">
                  <span className="pr-2 bg-white text-primary-grey-500">
                    Don't have an account yet?
                  </span>
                  <div className="text-center">
                    <a
                      className="font-medium text-primary-grey-500 focus:outline-none focus:underline transition ease-in-out duration-150"
                      href="https://www.cargoflip.com/free-trial-signup"
                    >
                      Register
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignIn;

SignIn.WithoutAuth = true;
SignIn.PageLayoutContainer = SimpleLayout;
